import { Component, ChangeDetectionStrategy, signal, inject, OnInit, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CalculationStorageService, CalculationListItem } from '../../services/calculation-storage.service';
import { DeleteConfirmationDialogComponent } from '../delete-confirmation-dialog/delete-confirmation-dialog.component';

@Component({
  selector: 'app-load-calculation-dialog',
  standalone: true,
  imports: [CommonModule, DeleteConfirmationDialogComponent],
  template: `
    @if (isOpen()) {
      <div class="dialog-overlay" (click)="close()">
        <div class="dialog-content" (click)="$event.stopPropagation()">
          <div class="dialog-header">
            <h2>Gespeicherte Berechnungen</h2>
            <button class="close-btn" (click)="close()" aria-label="Schließen">&times;</button>
          </div>
          
          <div class="dialog-body">
            @if (isLoading()) {
              <div class="loading">Lade Berechnungen...</div>
            } @else if (calculations().length === 0) {
              <div class="empty-state">
                <p>Sie haben noch keine Berechnungen gespeichert.</p>
              </div>
            } @else {
              <div class="calculations-list">
                @for (calc of calculations(); track calc.calculationId) {
                  <div class="calculation-item" (click)="selectCalculation(calc)">
                    <div class="calc-header">
                      <h3 [title]="calc.name">{{ calc.name }}</h3>
                      <button 
                        class="delete-btn" 
                        (click)="deleteCalculation(calc, $event)"
                        aria-label="Löschen"
                        title="Berechnung löschen"
                      >
                        🗑️
                      </button>
                    </div>
                    @if (calc.city || calc.address) {
                      <p class="calc-address">
                        @if (calc.city) { <span>{{ calc.city }}</span> }
                        @if (calc.city && calc.address) { <span> - </span> }
                        @if (calc.address) { <span>{{ calc.address }}</span> }
                      </p>
                    }
                    <div class="calc-meta">
                      <span class="meta-item">
                        Erstellt: {{ formatDate(calc.createdAt) }}
                      </span>
                      @if (calc.lastAccessedAt) {
                        <span class="meta-item">
                          Zuletzt geöffnet: {{ formatDate(calc.lastAccessedAt) }}
                        </span>
                      }
                    </div>
                  </div>
                }
              </div>
            }
          </div>
          
          <div class="dialog-footer">
            <button class="btn btn-secondary" (click)="close()">Schließen</button>
          </div>
        </div>
      </div>
    }
    
    <app-delete-confirmation-dialog />
  `,
  styles: [`
    .dialog-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    }
    
    .dialog-content {
      background: white;
      border-radius: 8px;
      max-width: 700px;
      width: 90%;
      max-height: 90vh;
      display: flex;
      flex-direction: column;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    }
    
    .dialog-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1.5rem;
      border-bottom: 1px solid #e5e7eb;
    }
    
    .dialog-header h2 {
      margin: 0;
      font-size: 1.5rem;
      color: #1f2937;
    }
    
    .close-btn {
      background: none;
      border: none;
      font-size: 2rem;
      color: #6b7280;
      cursor: pointer;
      padding: 0;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      transition: background-color 0.2s;
    }
    
    .close-btn:hover {
      background-color: #f3f4f6;
      color: #1f2937;
    }
    
    .dialog-body {
      padding: 1.5rem;
      overflow-y: auto;
      flex: 1;
    }
    
    .loading, .empty-state {
      text-align: center;
      padding: 2rem;
      color: #6b7280;
    }
    
    .calculations-list {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }
    
    .calculation-item {
      padding: 1rem;
      border: 2px solid #e5e7eb;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s;
    }
    
    .calculation-item:hover {
      border-color: var(--bright-blue, #3b82f6);
      background: #f9fafb;
    }
    
    .calc-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 0.5rem;
    }
    
    .calc-header h3 {
      margin: 0;
      font-size: 1.125rem;
      color: #1f2937;
      font-weight: 600;
      max-width: 500px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    
    .delete-btn {
      background: none;
      border: none;
      font-size: 1.25rem;
      cursor: pointer;
      padding: 0.25rem 0.5rem;
      border-radius: 4px;
      transition: background-color 0.2s;
    }
    
    .delete-btn:hover {
      background-color: #fee2e2;
    }
    
    .calc-address {
      margin: 0 0 0.5rem 0;
      color: #6b7280;
      font-size: 0.875rem;
    }
    
    .calc-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
      font-size: 0.75rem;
      color: #9ca3af;
    }
    
    .meta-item {
      display: inline-flex;
      align-items: center;
    }
    
    .dialog-footer {
      display: flex;
      gap: 0.75rem;
      justify-content: flex-end;
      padding: 1.5rem;
      border-top: 1px solid #e5e7eb;
    }
    
    .btn {
      padding: 0.75rem 1.5rem;
      border-radius: 6px;
      font-size: 1rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
      border: none;
    }
    
    .btn-secondary {
      background: #f3f4f6;
      color: #374151;
    }
    
    .btn-secondary:hover {
      background: #e5e7eb;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoadCalculationDialogComponent implements OnInit {
  private storageService = inject(CalculationStorageService);
  
  isOpen = signal(false);
  isLoading = signal(false);
  calculations = signal<CalculationListItem[]>([]);
  
  // Callback wenn Berechnung ausgewählt wurde
  onSelected = signal<((id: number) => void) | null>(null);
  
  // Callback für Toast-Nachrichten
  onToast = signal<((message: string) => void) | null>(null);
  
  // Callback wenn Berechnung gelöscht wurde
  onDeleted = signal<((id: number) => void) | null>(null);
  
  // ViewChild Referenz für Delete Confirmation Dialog
  deleteDialog = viewChild(DeleteConfirmationDialogComponent);
  
  ngOnInit() {
    // Wird beim Öffnen aufgerufen
  }
  
  async open() {
    this.isOpen.set(true);
    await this.loadCalculations();
  }
  
  async loadCalculations() {
    this.isLoading.set(true);
    try {
      const result = await this.storageService.getMyCalculations().toPromise();
      this.calculations.set(result || []);
    } catch (error) {
      console.error('Fehler beim Laden der Berechnungen:', error);
      this.calculations.set([]);
    } finally {
      this.isLoading.set(false);
    }
  }
  
  selectCalculation(calc: CalculationListItem) {
    const callback = this.onSelected();
    if (callback) {
      callback(calc.calculationId);
    }
    this.close();
  }
  
  deleteCalculation(calc: CalculationListItem, event: Event) {
    event.stopPropagation();
    
    const dialog = this.deleteDialog();
    if (!dialog) return;
    
    // Callback für Bestätigung setzen
    dialog.onConfirmed.set(async () => {
      try {
        await this.storageService.deleteCalculation(calc.calculationId).toPromise();
        await this.loadCalculations(); // Liste neu laden
        
        // Toast-Nachricht anzeigen
        const toastCallback = this.onToast();
        if (toastCallback) {
          toastCallback(`Berechnung "${calc.name}" wurde gelöscht`);
        }
        
        // onDeleted Callback aufrufen
        const deletedCallback = this.onDeleted();
        if (deletedCallback) {
          deletedCallback(calc.calculationId);
        }
        
        // Dialog schließen
        dialog.close();
      } catch (error) {
        console.error('Fehler beim Löschen:', error);
        const toastCallback = this.onToast();
        if (toastCallback) {
          toastCallback('Fehler beim Löschen der Berechnung');
        }
        dialog.close();
      }
    });
    
    // Dialog öffnen
    dialog.open(calc.name);
  }
  
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  
  close() {
    this.isOpen.set(false);
  }
}
